import { createContext, useContext, useState, useCallback, ReactNode } from 'react';
import { TutorialStep } from '../components/TutorialTooltip';
import { postSurveyFill } from '../services/survey/surveyService';
import { decodeTokenService } from '../services/decodeTokenService';

interface TutorialContextType {
  isWelcomeOpen: boolean;
  isTutorialActive: boolean;
  isPaused: boolean;
  currentStep: TutorialStep;
  currentStepIndex: number;
  totalSteps: number;
  steps: TutorialStep[];
  startTutorial: () => void;
  nextStep: () => void;
  completeCurrentStep: () => void;
  pauseTutorial: () => void;
  resumeTutorial: () => void;
  closeTutorial: () => void;
  restartTutorial: () => void;
  setSteps: (steps: TutorialStep[]) => void;
  setTutorialActions: (actions: Record<string, () => void>) => void;
  setCurrentStepIndex: (index: number) => void;
}

const TutorialContext = createContext<TutorialContextType | undefined>(undefined);

export function TutorialProvider({ children }: { children: ReactNode }) {
  const [steps, setSteps] = useState<TutorialStep[]>([]);
  const [isWelcomeOpen, setIsWelcomeOpen] = useState(true);
  const [isTutorialActive, setIsTutorialActive] = useState(false);
  const [isPaused, setIsPaused] = useState(false);
  const [currentStepIndex, setCurrentStepIndex] = useState(0);
  const [tutorialActions, setTutorialActions] = useState<Record<string, () => void>>({});

  const currentStep = isTutorialActive && currentStepIndex < steps.length
    ? steps[currentStepIndex]
    : steps[0];

  const handlePostSurveyResponse = ({ id, answer }: { id: string; answer: any }) => {
    const payload = {
      entity_type: "user",
      entity_id: decodeTokenService.getUserIdFromToken() as string,
      answers: {
        [id]: answer,
      }
    };
    postSurveyFill(payload, 'create-funnel-tutorial')
      .then((res) => {
        console.log(res);
      })
      .catch((err) => {
        console.error(err);
      });
  };

  const startTutorial = useCallback(() => {
    setIsWelcomeOpen(false);
    setIsTutorialActive(true);
    setCurrentStepIndex(0);
  }, []);

  const nextStep = useCallback(() => {
    setIsPaused(true);

    if (currentStepIndex < steps.length - 1) {
      const stepId = currentStep?.id;
      if (stepId && tutorialActions[stepId]) {
        tutorialActions[stepId]();
      }
    } else {
      const stepId = currentStep?.id;
      if (stepId && tutorialActions[stepId]) {
        tutorialActions[stepId]();
      }
      setIsTutorialActive(false);
      setIsPaused(false);
      setCurrentStepIndex(0);
    }
  }, [currentStepIndex, steps.length, currentStep, tutorialActions]);

  const completeCurrentStep = useCallback(() => {

    if (currentStepIndex < steps.length - 1) {
      setCurrentStepIndex(prev => prev + 1);
      setIsPaused(false);
    } else {
      setIsTutorialActive(false);
      setIsPaused(false);
      setCurrentStepIndex(0);
    }
  }, [currentStepIndex, steps.length, currentStep]);

  const pauseTutorial = useCallback(() => {
    setIsPaused(true);
    setIsTutorialActive(false);
  }, []);

  const resumeTutorial = useCallback(() => {
    setIsPaused(false);
    setIsTutorialActive(true);
  }, []);

  const closeTutorial = useCallback(() => {
    setIsTutorialActive(false);
    setIsWelcomeOpen(false);
    setIsPaused(false);
    setCurrentStepIndex(0);
  }, []);

  const restartTutorial = useCallback(() => {
    setIsWelcomeOpen(true);
    setIsTutorialActive(false);
    setIsPaused(false);
    setCurrentStepIndex(0);
  }, []);

  return (
    <TutorialContext.Provider
      value={{
        isWelcomeOpen,
        isTutorialActive,
        isPaused,
        currentStep,
        currentStepIndex,
        totalSteps: steps.length,
        steps,
        startTutorial,
        nextStep,
        completeCurrentStep,
        pauseTutorial,
        resumeTutorial,
        closeTutorial,
        restartTutorial,
        setSteps,
        setTutorialActions,
        setCurrentStepIndex,
      }}
    >
      {children}
    </TutorialContext.Provider>
  );
}

export function useTutorialContext() {
  const context = useContext(TutorialContext);
  if (context === undefined) {
    throw new Error('useTutorialContext must be used within a TutorialProvider');
  }
  return context;
}
